import { useCallback, useState, useEffect } from "react";
import HttpLeadRepository from "../api/request/Lead/Lead.service";
import { DashboardDataI, PricingI } from "../api/request/Lead/Interfaces/PricingInterface";
import { PolicyI } from "../api/request/Lead/Interfaces/PolicyInterface";
import { DocumentsI } from "../api/request/Lead/Interfaces/DocumentsInterface";
import { PersonProjectI } from "../api/request/Lead/Interfaces/PersonProjectInterface";
import { customerProjectsEnabled } from "../native/nativeMode";

/**
 * useDashboardData Hook
 *
 * Custom hook that fetches dashboard data from BFF endpoints concurrently using Promise.all()
 * to avoid waterfall effect and improve performance. The documents call is wrapped so a
 * failure there does not break the rest of the dashboard data.
 *
 * @param leadId - Lead ULID to fetch data for
 * @param token - JWT authentication token
 * `reload()` vuelve a pedir los datos del mismo lead sin tapar los que ya hay (tras
 * cambiar el capital de un preventivo, 04-nov-2026). Si en la recarga falla la lista
 * de preventivos, se conserva la anterior en vez de esconder el panel.
 *
 * @returns Object containing data, loading state, error and reload
 *
 * @example
 * ```tsx
 * const { data, loading, error } = useDashboardData(leadId, token);
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return (
 *   <Dashboard
 *     beneficiaries={data.beneficiary}
 *     policy={data.policy}
 *     pricing={data.pricing}
 *   />
 * );
 * ```
 */
export const useDashboardData = (leadId: string, token: string) => {
  const [result, setResult] = useState<{ leadId: string; token: string; data: DashboardDataI } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let current = true;
    // Otro lead o token: fuera lo de antes. Una recarga del mismo: se queda a la
    // vista mientras llega lo nuevo.
    setResult((prev) => (prev?.leadId === leadId && prev?.token === token ? prev : null));
    const leadService = new HttpLeadRepository(leadId, token);

    const fetchDocumentsSafe = async (): Promise<DocumentsI | null> => {
      try {
        return await leadService.getDocuments();
      } catch {
        return null;
      }
    };

    // Portal Cliente como punto de entrada único (2026-10-15): gated por flag
    // (ver nativeMode.ts) y safe-wrapped como documents — el contrato del
    // gateway está pendiente, así que un fallo aquí no debe romper el resto
    // del dashboard (póliza activa, pricing, etc.).
    const fetchProjectsSafe = async (): Promise<PersonProjectI[] | null> => {
      if (!customerProjectsEnabled()) {
        return null;
      }
      try {
        return await leadService.getPersonProjects();
      } catch {
        return null;
      }
    };

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      // Modo cotizaciones: el lead de la sesión puede no ser una póliza (cotización a
      // medias). Póliza/precio/beneficiarios pasan a ser opcionales para que su fallo
      // no tumbe el Dashboard ni esconda la lista de cotizaciones. Fuera de ese modo,
      // comportamiento de siempre (un fallo es error del Dashboard).
      const optional = customerProjectsEnabled();
      const soft = async <T,>(call: () => Promise<T>, fallback: T): Promise<T> => {
        if (!optional) return call();
        try {
          return await call();
        } catch {
          return fallback;
        }
      };

      try {
        // Concurrent fetch using Promise.all() to avoid waterfall effect
        const [beneficiary, policy, pricing, documents, projects] = await Promise.all([
          soft(() => leadService.getBeneficiaryDashboard(), []),
          soft<PolicyI | undefined>(() => leadService.getPolicyDashboard(), undefined),
          soft<PricingI | undefined>(() => leadService.getPricingDashboard(), undefined),
          fetchDocumentsSafe(),
          fetchProjectsSafe(),
        ]);

        if (current) {
          setResult((prev) => {
            const samePrevious = prev?.leadId === leadId && prev?.token === token;
            const keptProjects =
              null === projects && samePrevious ? prev!.data.projects ?? null : projects;
            return {
              leadId,
              token,
              data: { beneficiary, policy, pricing, documents, projects: keptProjects },
            };
          });
        }
      } catch (err) {
        if (current) setError(err instanceof Error ? err : new Error("Failed to fetch dashboard data"));
      } finally {
        if (current) setLoading(false);
      }
    };

    if (leadId && token) {
      fetchData();
    } else {
      setLoading(false);
      setError(null);
    }
    return () => { current = false; };
  }, [leadId, token, version]);

  // Mask the previous principal immediately, before the next effect runs.
  const samePrincipal = result?.leadId === leadId && result?.token === token;
  return { data: samePrincipal ? result!.data : null, loading, error, reload };
};
